/**
 * Convierte una imagen (objeto File) a formato WebP.
 * Si no es una imagen, retorna el archivo original.
 * @param {File} file - Archivo de imagen a convertir.
 * @param {Object} [options] - Opciones de conversión.
 * @param {number} [options.maxWidth=1024] - Ancho máximo de la imagen resultante.
 * @param {number} [options.maxHeight=1024] - Alto máximo de la imagen resultante.
 * @param {number} [options.quality=0.8] - Calidad de la conversión WebP (entre 0 y 1).
 * @returns {Promise<File>} - Promesa que resuelve con el nuevo File en formato WebP o el original si no es imagen.
 */
export async function convertImageToWebp(file, options = {}) {
  const { maxWidth = 1024, maxHeight = 1024, quality = 0.8 } = options

  // Si no es imagen, devuelve el archivo original.
  if (!file.type.startsWith('image/')) {
    return file
  }

  // Si ya es WebP, retorna el archivo original.
  if (file.type === 'image/webp') {
    return file
  }

  // Creamos un elemento imagen para cargar la URL de datos.
  const img = new Image()
  const objectUrl = URL.createObjectURL(file)

  return new Promise((resolve, reject) => {
    img.onload = () => {
      // Se obtienen las dimensiones originales.
      let width = img.width
      let height = img.height

      // Redimensiona manteniendo la relación de aspecto si es mayor al tamaño máximo.
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height
        if (width > height) {
          width = maxWidth
          height = maxWidth / aspectRatio
        } else {
          height = maxHeight
          width = maxHeight * aspectRatio
        }
      }

      // Se crea el canvas con las dimensiones definidas.
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        URL.revokeObjectURL(objectUrl)
        return reject(new Error(`No se pudo procesar la imagen ${file.name}: canvas no disponible`))
      }

      ctx.drawImage(img, 0, 0, width, height)

      // Se convierte el canvas a blob en formato WebP.
      canvas.toBlob(
        blob => {
          URL.revokeObjectURL(objectUrl)
          canvas.width = 0
          canvas.height = 0

          if (blob) {
            const webpFile = new File(
              [blob],
              file.name.replace(/\.\w+$/, '') + '.webp',
              { type: 'image/webp' }
            )
            resolve(webpFile)
          } else {
            reject(new Error(`La conversión de la imagen ${file.name} falló`))
          }
        },
        'image/webp',
        quality
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      reject(new Error(`No se pudo leer o decodificar la imagen ${file.name}`))
    }

    // Se asigna una object URL para evitar cargar todo el archivo como base64 antes de tiempo.
    img.src = objectUrl
  })
}

// devuelve un objeto con el ancho y alto de una imagen en base64
export async function getDims(base64) {
  return await new Promise(resolve => {
    let img = new Image()
    img.src = base64
    img.onload = () => {
      resolve({
        ancho: img.width,
        alto: img.height
      })
    }
  })
}
